import moment from 'moment'
import {
  WorkoutApiParams,
  workoutsApi,
} from '../../../../common/networking/workouts'
import { reportEmbeddedFailure } from '../../../../common/utility/embeddedErrorReporter'
import configuration from '../../../../configuration'
import { AppThunk, RootState } from '../../../store'
import { Workout } from '../onDemandWorkouts'
import {
  WorkoutsJsonSuccessResponse,
  getWorkoutsFailure,
  getWorkoutsPending,
  getWorkoutsSuccess,
} from '.'

const { fetchWorkouts } = workoutsApi()
let latestWorkoutsRequestId = 0

const { workoutAnticipationMinutes } = configuration

export const getWorkouts =
  ({
    isOnDemandFilter = true,
    dayOfWeekFilter,
    trainerId,
    hasMobileAppAccess = false,
  }: {
    isOnDemandFilter?: boolean
    dayOfWeekFilter?: number
    trainerId?: string
    hasMobileAppAccess?: boolean
  }): AppThunk =>
  async (dispatch, getState) => {
    const requestId = ++latestWorkoutsRequestId
    const { auth, workouts, filters } = getState() as RootState
    const authToken = auth?.token
    const page =
      typeof workouts.lastPageFetched === 'undefined'
        ? 0
        : workouts.lastPageFetched + 1
    dispatch(getWorkoutsPending(true))
    try {
      const {
        tagFilters,
        favoriteFilter,
        hideCompletedFilter,
        timesFilter: { startTimeFilter, endTimeFilter },
        timeFilter,
      } = filters

      const response = await fetchWorkouts({
        tagFilters,
        favoriteFilter,
        hideCompletedFilter,
        startTimeFilter,
        endTimeFilter,
        authToken,
        page,
        isOnDemandFilter,
        dayOfWeekFilter,
        timeFilter,
        trainerId,
        hasMobileAppAccess,
      } as WorkoutApiParams)

      const finalResponse = parseResponseByFilterType({
        response,
        isOnDemandFilter,
      })

      if (requestId !== latestWorkoutsRequestId) return

      dispatch(getWorkoutsSuccess(finalResponse))
    } catch (error: any) {
      if (requestId !== latestWorkoutsRequestId) return

      reportEmbeddedFailure({
        category: 'workout_data',
        message: error instanceof Error ? error.message : String(error),
        name: 'WorkoutListLoadError',
      })
      dispatch(getWorkoutsFailure(error.toString()))
    }
  }

const updateStartEndTimes = (workouts: Workout[]): Workout[] =>
  workouts.map((workout) => {
    const dateStartLive = moment
      .utc(workout.startTime)
      .subtract(workoutAnticipationMinutes, 'minutes')
    const dateEndLive = moment
      .utc(workout.startTime)
      .add(workout.duration, 'minutes')
    const usersCurrentTime = moment.utc()
    workout.isLive = usersCurrentTime.isBetween(dateStartLive, dateEndLive)
    workout.isOver = usersCurrentTime.isAfter(dateEndLive)

    return workout
  })

export const parseResponseByFilterType = ({
  response,
  isOnDemandFilter,
}: {
  response: WorkoutsJsonSuccessResponse
  isOnDemandFilter: boolean
}) => {
  if (isOnDemandFilter) {
    return response
  } else {
    const availableWorkouts =
      response.content && updateStartEndTimes(response.content)
    const workoutsWithLiveTimes = availableWorkouts.filter(
      (workout) => !workout.isOver,
    )

    const modifiedResponse: WorkoutsJsonSuccessResponse = {
      ...response,
      content: workoutsWithLiveTimes || [],
    }

    if (!workoutsWithLiveTimes.length)
      modifiedResponse.allWorkoutsAreOver = true

    return modifiedResponse
  }
}
